import dayjs from '../../utils/dayjs';
import { FormLayout, Modal, TextField, Form, Text, Checkbox, Icon, SkeletonBodyText, Select } from '@shopify/polaris';
import helpers from 'helpers';
import { lengthLessThan, notEmpty, notEmptyString, useField, useForm } from '@shopify/react-form';
import __ from 'languages/index';
import { TypedTripData, useMutateAddTimeToTrip } from 'queries/trip.query';
import { ClockIcon } from '@shopify/polaris-icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useGetSeatings } from 'queries/seating.query';


export default function TripTimeModal({
  onClose,
  show,
  current_journey_id,
  current_departure_date,
}: {
  onClose: (output: TypedTripData | null) => void;
  show: boolean;
  current_journey_id: string | bigint;
  current_departure_date: Date;
}) {
  const { mutateAsync: createEntity, isPending: creating, isSuccess: creatingSuccess } = useMutateAddTimeToTrip();
  const [seatingOptions, setSeatingOptions] = useState([]);
  const [defaultTime, setDefaultTime] = useState(() => dayjs(new Date()).format('HH:mm'));
  const { data: seatingTemplateData, isLoading: seatingLoading } = useGetSeatings({
    limit: 20,
    sort: 'createdAt:desc',
  });

  useEffect(() => {
    if (!helpers.isEmpty(seatingTemplateData)) {
      let { body } = seatingTemplateData;
      let totalResult = [
        {
          label: 'Chọn 1 loại hình',
          value: '',
        },
      ];
      for (let seating of body) {
        totalResult.push({
          label: seating.seating_name,
          value: seating.id,
        });
      }

      setSeatingOptions(totalResult);
    }
  }, [seatingTemplateData]);

  const useFields = {
    time: useField<string>({
      value: defaultTime,
      validates: [
        notEmpty('Không được để trống trường này.'),
        lengthLessThan(250, 'No more than 250 characters.'),
        (inputValue) => {
          if (inputValue && !helpers.isValidTime(inputValue)) return 'Giờ định dạng sai (hh:mm)';
        },
      ],
    }),

    price: useField<number>({
      value: 0,
      validates: [
        (inputVal) => {
          if (!helpers.isNumber(inputVal)) return 'Phải là một số hợp lệ.';
        },
      ],
    }),
    shipping_price: useField<number>({
      value: 0,
      validates: [
        (inputVal) => {
          if (!helpers.isNumber(inputVal)) return 'Phải là một số hợp lệ.';
        },
      ],
    }),

    seating_id: useField<string>({ value: '', validates: [notEmptyString('Không được để trống trường này.')] }),
  };

  const { fields, submit, dirty } = useForm({
    fields: useFields,

    async onSubmit(values) {
      try {
        // create new
        let createdData = await createEntity({
          time: values.time,
          price: helpers.parseNumeric(values.price),
          shipping_price: helpers.parseNumeric(values.shipping_price),
          journey_id: String(current_journey_id),
          departure_date: `` + current_departure_date.getTime(),
          seating_id: values.seating_id || null,
        });
        helpers.resetForm(useFields);
        onClose(createdData);
      } catch (e: any) {
        if (e.params !== undefined) {
          if (e.params.field !== undefined) {
            useFields[e.params.field].setError(__('check_this_field_again'));
          }
        }
      }
      return { status: 'success' };
    },
  });

  /** Tự động format giờ */
  const prevDigitsRef = useRef('');

  useEffect(() => {
    if (show) {
      const currentTime = dayjs(new Date()).format('HH:mm');
      setDefaultTime(currentTime);
      fields.time.onChange(currentTime);
      prevDigitsRef.current = currentTime.replace(/\D/g, '');
      return;
    }

    helpers.resetForm(useFields);
    prevDigitsRef.current = '';
  }, [show]);

  /* End form */

  /** Khi chọn một seating Template, hơi dài dòng */
  const changeSeatingTemplateCallback = useCallback(
    (idSelected: any) => {
      fields.seating_id.onChange(idSelected);
    },
    [seatingTemplateData]
  );

  const handleTimeChange = (raw) => {
    const digits = raw.replace(/\D/g, '').slice(0, 4);
    const prevDigits = prevDigitsRef.current;
    const isAdding = digits.length > prevDigits.length;

    let formatted = raw;

    if (isAdding) {
      // chỉ format khi đang gõ thêm
      formatted = digits.length > 2 ? `${digits.slice(0, 2)}:${digits.slice(2)}` : digits;
    } else {
      // nếu đang xóa: giữ nguyên dấu ':' nếu nó đang có trong raw
      if (raw.includes(':')) {
        formatted = raw;
      } else {
        // fallback: format lại theo digits để tránh lộn xộn
        formatted = digits.length > 2 ? `${digits.slice(0, 2)}:${digits.slice(2)}` : digits;
      }
    }

    prevDigitsRef.current = digits;
    return formatted;
  };

  const Actual_page = (
    <Modal
      open={show}
      onClose={() => onClose(null)}
      title={'Thêm giờ chạy'}
      primaryAction={{
        content: 'Thêm giờ chạy',
        disabled: !dirty,
        loading: creating,
        onAction: submit,
      }}
      secondaryActions={[
        {
          content: 'Close',
          onAction: () => onClose(null),
        },
      ]}
    >
      <Modal.Section>
        <Form onSubmit={submit}>
          <FormLayout>
            <TextField
              autoFocus
              autoComplete="off"
              label="Giờ chạy"
              placeholder=""
              suffix="hh:mm"
              maxLength={5}
              prefix={<Icon source={ClockIcon} />}
              {...fields.time}
              value={fields.time.value}
              onChange={(value) => fields.time.onChange(handleTimeChange(value))}
              clearButton
              onClearButtonClick={() => fields.time.onChange('')}
            />

            <FormLayout.Group>
              <TextField
                label="Giá khách (VND)"
                autoComplete="off"
                {...fields.price}
                value={helpers.formatNumberWithCommas(fields.price.value)}
                onChange={(a) => fields.price.onChange(helpers.parseNumeric(a))}
              />
              <TextField
                label="Giá gửi hàng (VND)"
                autoComplete="off"
                {...fields.shipping_price}
                value={helpers.formatNumberWithCommas(fields.shipping_price.value)}
                onChange={(a) => fields.shipping_price.onChange(helpers.parseNumeric(a))}
              />
            </FormLayout.Group>

            {/* 4. Thay thế TextField bằng Select */}
            {seatingLoading ? (
              <SkeletonBodyText />
            ) : (
              <Select
                label="Loại hình vận chuyển?"
                requiredIndicator
                options={seatingOptions}
                onChange={changeSeatingTemplateCallback}
                value={fields.seating_id.value} // Giá trị của Select là ID (lấy từ trường _geo)
                error={fields.seating_id.error} // Hiển thị lỗi từ trường _geo
              />
            )}

            <Text as="p" tone="critical">
              *Có thể ghi đè giá mặc định hoặc loại hình vận chuyển của giờ chạy này. <br />
              *Giờ chạy thêm tại đây chỉ hiển thị cho ngày này và hành trình này, không hiển thị ở các hành trình khác.
            </Text>
          </FormLayout>
        </Form>
      </Modal.Section>
    </Modal>
  );

  return <>{Actual_page}</>;
}

